import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router';

function MenuIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return isActive
    ? 'public__mobile-nav-link public__mobile-nav-link--active'
    : 'public__mobile-nav-link';
}

/**
 * Hamburger toggle standing in for `.public__nav` and `.public__header-actions`
 * together below the `.public__nav` breakpoint (see styles.css's responsive
 * floor), where both hide outright with nothing replacing them. This isn't a
 * second menu — the dropdown holds the exact same destinations as the
 * desktop header: the three nav links, "Help Center" (still inert, matching
 * its desktop counterpart), and the two portal login doors `AccountMenu`
 * otherwise nests behind "My Account" — flattened here rather than nesting
 * a second dropdown trigger inside this one.
 *
 * Same open/outside-click/Escape shape as `AccountMenu`.
 */
export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function close() {
    setOpen(false);
  }

  return (
    <div className="public__mobile-menu" ref={menuRef}>
      <button
        type="button"
        className="public__menu-toggle"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Menu"
        onClick={() => setOpen((value) => !value)}
      >
        <MenuIcon />
      </button>

      {open ? (
        <nav className="public__mobile-nav" aria-label="Listings" role="menu">
          <NavLink to="/new-rentals" className={navLinkClassName} role="menuitem" onClick={close}>
            New Rentals
          </NavLink>
          <NavLink to="/listings" className={navLinkClassName} role="menuitem" onClick={close}>
            Rental Finder
          </NavLink>
          <NavLink
            to="/hire-a-property-manager"
            className={navLinkClassName}
            role="menuitem"
            onClick={close}
          >
            Hire a Property Manager
          </NavLink>
          <span className="public__mobile-nav-link public__mobile-nav-link--inert">
            Help Center
          </span>
          <hr className="public__mobile-nav-divider" />
          <Link to="/login" className="button public__account-pill" role="menuitem" onClick={close}>
            Resident Login
          </Link>
          <Link
            to="/login/owner"
            className="button button--quiet public__account-pill"
            role="menuitem"
            onClick={close}
          >
            Owner Login
          </Link>
        </nav>
      ) : null}
    </div>
  );
}
